import React, { useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  EyeSlashIcon,
} from '@heroicons/react/24/outline';

interface AnonymizationResult {
  success: boolean;
  dryRun: boolean;
  processedCount: number;
  skippedCount: number;
  failedCount: number;
  anonymizedAmount: number;
  errors: Array<{ collaboratorId: string; error: string }>;
  timestamp: string;
  environment: string;
}

const AnonymizeSalaryData: React.FC = () => {
  const [isDryRun, setIsDryRun] = useState(true);
  const [isRunning, setIsRunning] = useState(false);
  const [result, setResult] = useState<AnonymizationResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [anonymizedAmount, setAnonymizedAmount] = useState<number>(1000);

  // Check if we're in development environment
  const isDevelopment = import.meta.env.DEV ||
    window.location.hostname === 'localhost' ||
    import.meta.env.VITE_REACT_FIREBASE_PROJECT_ID?.includes('dev');

  const runAnonymization = async () => {
    if (!isDevelopment && !isDryRun) {
      setError('Anonimização só pode ser executada em ambiente de desenvolvimento!');
      return;
    }

    if (!isDryRun && !window.confirm(
      `⚠️ ATENÇÃO: Esta ação vai substituir TODOS os valores salariais por R$ ${anonymizedAmount.toFixed(2)}.\n\n` +
      'Esta ação NÃO PODE ser desfeita facilmente!\n\n' +
      'Você tem certeza que deseja continuar?'
    )) {
      return;
    }

    setIsRunning(true);
    setError(null);
    setResult(null);

    try {
      const functions = getFunctions();
      const anonymizeSalaryData = httpsCallable<
        { dryRun: boolean; anonymizedAmount: number },
        AnonymizationResult
      >(functions, 'anonymizeSalaryData');

      console.log('Calling anonymizeSalaryData function...', {
        dryRun: isDryRun,
        anonymizedAmount
      });

      const response = await anonymizeSalaryData({
        dryRun: isDryRun,
        anonymizedAmount: anonymizedAmount,
      });

      console.log('Anonymization response:', response.data);
      setResult(response.data);

    } catch (err: any) {
      console.error('Anonymization error:', err);
      setError(
        err.message ||
        'Erro ao executar anonimização. Verifique o console para mais detalhes.'
      );
    } finally {
      setIsRunning(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Environment Warning */}
      {!isDevelopment && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">
                <strong>AMBIENTE DE PRODUÇÃO DETECTADO!</strong>
                <br />
                Esta migração só pode ser executada em ambiente de desenvolvimento.
                Dry-run ainda está disponível para visualização.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Description */}
      <div className="prose prose-sm max-w-none">
        <h3 className="text-lg font-medium text-gray-900">
          Anonimização de Dados Salariais
        </h3>
        <p className="text-gray-600">
          Esta migração substitui todos os valores salariais por um valor fixo (padrão: R$ 1.000,00).
          <br />
          <strong>Projeto Destino:</strong> <span className="text-blue-600">{import.meta.env.VITE_REACT_FIREBASE_PROJECT_ID}</span>
        </p>
        <div className="bg-blue-50 border-l-4 border-blue-400 p-4 my-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <EyeSlashIcon className="h-5 w-5 text-blue-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-blue-700">
                <strong>Caso de Uso:</strong> Após restaurar backup de produção em desenvolvimento,
                execute esta função para anonimizar dados sensíveis.
              </p>
            </div>
          </div>
        </div>
        <h4 className="text-md font-medium text-gray-900 mt-4">O que será anonimizado:</h4>
        <ul className="list-disc list-inside text-gray-600 space-y-1">
          <li>Valores de salário atual (newSalaryEntryAmount)</li>
          <li>Valores de extras salariais (zerando)</li>
          <li>Todo o histórico de salários (salaryHistorySalaries)</li>
          <li>Dados na subcoleção salary/* (se já migrados)</li>
        </ul>
        <h4 className="text-md font-medium text-gray-900 mt-4">O que NÃO será alterado:</h4>
        <ul className="list-disc list-inside text-gray-600 space-y-1">
          <li>Datas de alteração salarial</li>
          <li>Tipos de contrato (CLT, PJ, etc)</li>
          <li>Motivos de reajuste</li>
          <li>Observações</li>
        </ul>
      </div>

      {/* Configuration */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <h4 className="text-sm font-medium text-gray-900 mb-3">Configuração</h4>
        <div className="space-y-4">
          {/* Anonymized Amount */}
          <div>
            <label htmlFor="amount" className="block text-sm font-medium text-gray-700">
              Valor Anonimizado (R$)
            </label>
            <input
              type="number"
              id="amount"
              value={anonymizedAmount}
              onChange={(e) => setAnonymizedAmount(parseFloat(e.target.value) || 1000)}
              min="0"
              step="0.01"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              disabled={isRunning}
            />
            <p className="mt-1 text-xs text-gray-500">
              Todos os salários serão substituídos por este valor
            </p>
          </div>

          {/* Dry Run Toggle */}
          <div className="flex items-center">
            <input
              id="dry-run"
              type="checkbox"
              checked={isDryRun}
              onChange={(e) => setIsDryRun(e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              disabled={isRunning}
            />
            <label htmlFor="dry-run" className="ml-2 block text-sm text-gray-900">
              Dry Run (simular sem modificar dados)
            </label>
          </div>
        </div>
      </div>

      {/* Action Button */}
      <div className="flex items-center justify-between">
        <button
          onClick={runAnonymization}
          disabled={isRunning || (!isDevelopment && !isDryRun)}
          className={`
            px-4 py-2 rounded-md font-medium text-white
            ${isRunning
              ? 'bg-gray-400 cursor-not-allowed'
              : isDryRun
                ? 'bg-blue-600 hover:bg-blue-700'
                : 'bg-red-600 hover:bg-red-700'
            }
            disabled:bg-gray-400 disabled:cursor-not-allowed
            transition-colors
          `}
        >
          {isRunning ? (
            <>
              <svg
                className="animate-spin -ml-1 mr-3 h-5 w-5 text-white inline"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              Executando...
            </>
          ) : isDryRun ? (
            'Executar Dry Run'
          ) : (
            '⚠️ Executar Anonimização'
          )}
        </button>

        {!isDevelopment && !isDryRun && (
          <span className="text-sm text-red-600">
            Execução bloqueada em produção
          </span>
        )}
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <XCircleIcon className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Result Display */}
      {result && (
        <div
          className={`border-l-4 p-4 ${
            result.success
              ? 'bg-green-50 border-green-400'
              : 'bg-red-50 border-red-400'
          }`}
        >
          <div className="flex">
            <div className="flex-shrink-0">
              {result.success ? (
                <CheckCircleIcon className="h-5 w-5 text-green-400" />
              ) : (
                <XCircleIcon className="h-5 w-5 text-red-400" />
              )}
            </div>
            <div className="ml-3 flex-1">
              <h3
                className={`text-sm font-medium ${
                  result.success ? 'text-green-800' : 'text-red-800'
                }`}
              >
                {result.dryRun
                  ? '✅ Dry Run Concluído'
                  : result.success
                    ? '✅ Anonimização Concluída'
                    : '❌ Anonimização Falhou'}
              </h3>
              <div className="mt-2 text-sm text-gray-700">
                <dl className="grid grid-cols-2 gap-2">
                  <dt className="font-medium">Ambiente:</dt>
                  <dd>{result.environment}</dd>

                  <dt className="font-medium">Valor Anonimizado:</dt>
                  <dd>R$ {result.anonymizedAmount.toFixed(2)}</dd>

                  <dt className="font-medium">Processados:</dt>
                  <dd>{result.processedCount}</dd>

                  <dt className="font-medium">Ignorados:</dt>
                  <dd>{result.skippedCount}</dd>

                  <dt className="font-medium">Com Erro:</dt>
                  <dd>{result.failedCount}</dd>

                  <dt className="font-medium">Timestamp:</dt>
                  <dd>{new Date(result.timestamp).toLocaleString('pt-BR')}</dd>
                </dl>

                {result.errors.length > 0 && (
                  <div className="mt-4">
                    <p className="font-medium text-red-800">Erros:</p>
                    <ul className="list-disc list-inside mt-2 space-y-1">
                      {result.errors.map((err, idx) => (
                        <li key={idx} className="text-xs text-red-700">
                          {err.collaboratorId}: {err.error}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {result.dryRun && (
                  <p className="mt-4 text-blue-700 font-medium">
                    ℹ️ Nenhuma alteração foi feita (dry run). Desmarque a opção e execute
                    novamente para aplicar as mudanças.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnonymizeSalaryData;
